import {
  Modal,
  ModalContent,
  ModalOverlay,
  ModalHeader,
  ModalBody,
  Flex,
  Box,
  SimpleGrid,
  Text,
  Table,
  Th,
  Thead,
  Tr,
  Tbody,
  Td,
  Image,
  ModalCloseButton,
} from "@chakra-ui/react";
import moment from "moment";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { numberToDollar } from "utils/helpers";
import { stringToFullNumber } from "utils/helpers";
import { getSidebarBackground } from "utils/uiconfighelper";
import { useCustomFormats } from "hooks/useCustomFormat";
import { ActionButtons } from "../components/ActionButtons";
import jsPDF from "jspdf";
import { useStatement } from "hooks/statements/useStatement";

export interface Activity {
  date: Date;
  transaction: string;
  type: string;
  amount: number;
}

export interface MonthlyStatementsModalProps {
  isOpen: boolean;
  onClose: () => void;
  months: string;
}

export function MonthlyStatementsModal({ isOpen, onClose, months }: MonthlyStatementsModalProps) {
  const { loan } = useContext(LoanContext);
  const { user } = useTenant();
  const { formatDate } = useCustomFormats();

  const printRef = useRef<HTMLDivElement>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [ready, setReady] = useState(false);
  const [pdf, setPdf] = useState<jsPDF>();

  const custom_currency =
    user?.company?.company_configurations?.[0]?.custom_currency?.currency || "$";

  const formattedToday = moment().format("D-MMM");

  const passingMonthCount = Number(months.split(" ")[0]);

  const statementName = useMemo(() => `Monthly Statement for ${months}`, [months]);

  const formattedTodaysDate = moment().format("YYYY-MM-DD");
  const substractedDate = moment().subtract(passingMonthCount, "months").format("YYYY-MM-DD");

  const formatCurrency = useCallback(
    (amount: number) => {
      return custom_currency
        ? numberToDollar(stringToFullNumber(amount.toString()), custom_currency)
        : numberToDollar(stringToFullNumber(amount.toString()));
    },
    [custom_currency]
  );

  const getRemainingBalance = useCallback(() => {
    const totalDraws =
      loan?.draw_requests.reduce((acc, drawRequest) => {
        return acc + drawRequest.amount;
      }, 0) ?? 0;

    const totalFees =
      loan?.fees.reduce((acc, fee) => {
        return acc + (fee.fee_amount ?? 0);
      }, 0) ?? 0;

    const remainingBalance = Number(loan?.amount) - totalDraws - totalFees;
    return formatCurrency(remainingBalance);
  }, [loan, formatCurrency]);

  const dateRange = useCallback((passingMonthCount: any) => {
    return moment().subtract(passingMonthCount, "months").format("D-MMM");
  }, []);

  const isWithinRange = useCallback(
    (dateString: any) => {
      const date = moment(dateString);
      return date.isBetween(substractedDate, formattedTodaysDate, undefined, "[]");
    },
    [substractedDate, formattedTodaysDate]
  );

  useEffect(() => {
    if (!loan) return;

    const activities: Activity[] = [];

    loan.draw_requests
      .filter(drawRequest => drawRequest.status === "paid")
      .filter(drawRequest => isWithinRange(drawRequest.due_date))
      .forEach(drawRequest => {
        activities.push({
          date: moment(drawRequest.due_date, "YYYY-MM-DD").toDate(),
          transaction: drawRequest.title,
          type: "Draw",
          amount: drawRequest.amount,
        });
      });

    loan.accounts_receivable
      .filter(payment => isWithinRange(payment.payment_date))
      .forEach(payment => {
        activities.push({
          date: moment(payment.payment_date, "YYYY-MM-DD").toDate(),
          transaction: payment.description ?? "",
          type: "Payment",
          amount: payment.payment_amount ?? 0,
        });
      });

    loan.fees
      .filter(fee => isWithinRange(fee.fee_date))
      .forEach(fee => {
        activities.push({
          date: moment(fee.fee_date, "YYYY-MM-DD").toDate(),
          transaction: fee.description ?? "",
          type: "Fee",
          amount: fee.fee_amount ?? 0,
        });
      });

    setActivities(activities.sort((a, b) => a.date.getTime() - b.date.getTime()));
    setReady(true);
  }, [loan, isWithinRange]);

  const { save: saveStatement, isSaving } = useStatement();

  useEffect(() => {
    if (loan?.id && printRef.current && ready) {
      saveStatement({
        element: printRef.current,
        name: statementName,
        loanId: loan.id,
      }).then(pdf => {
        setPdf(pdf);
      });
    }
  }, [loan?.id, printRef, saveStatement, statementName, ready]);

  function subTitle(title: string) {
    return (
      <Box className="statement-subtitle" background="#5A5A5A" color="white" p={2}>
        {title}
      </Box>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader display="flex" alignItems="center">
          Monthly Statements
          <ActionButtons pdf={pdf} isSaving={isSaving} isLoading={false} fileName={statementName} />
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap={4} height="full" className="print-area" ref={printRef}>
            <Flex direction="row" gap={4} justifyContent="space-between">
              {loan && (
                <Box alignSelf="start">
                  <Image
                    src={loan.lender_company.logo ?? ""}
                    alt="company-logo"
                    background={getSidebarBackground(user)}
                    width={"300px"}
                  />
                </Box>
              )}
              <Flex direction="column" gap={4} alignItems="flex-end">
                <SimpleGrid columns={2} gap={1}>
                  <Text fontWeight="medium" color="black">
                    Loan Number
                  </Text>
                  <Text textAlign="right">#{loan?.id.split("-")[0]}</Text>

                  <Text fontWeight="medium" color="black">
                    Funding Date
                  </Text>
                  <Text textAlign="right">{moment(loan?.funding_date).format("MMM D, YYYY")}</Text>
                </SimpleGrid>
                <Box>
                  <Text fontWeight="bold" color="black" fontSize="2xl">
                    {formatCurrency(Number(loan?.amount ?? 0))}
                  </Text>
                </Box>
              </Flex>
            </Flex>
            <Flex direction="column" gap={4} width="100%" overflow="hidden">
              {subTitle("Current Payment")}
              <Flex direction="column" gap={4} px={2}>
                <Box borderBottomWidth="1px" fontWeight={"650"}>
                  Account information
                </Box>
                <SimpleGrid columns={2} gap={1}>
                  <Text fontWeight="medium" color="black">
                    Statement Date
                  </Text>
                  <Text textAlign="right">{moment().format("MMM D, YYYY")}</Text>

                  <Text fontWeight="medium" color="black">
                    Loan Status
                  </Text>
                  <Text textAlign="right">{loan?.status.label}</Text>

                  <Text fontWeight="medium" color="black">
                    Remaining Principal
                  </Text>
                  <Text textAlign="right">{getRemainingBalance()}</Text>

                  <Text fontWeight="medium" color="black">
                    Interest Rate (Fixed)
                  </Text>
                  <Text textAlign="right">{loan?.interest_rate}%</Text>

                  <Text fontWeight="medium" color="black">
                    Funding Date
                  </Text>
                  <Text textAlign="right">{moment(loan?.funding_date).format("MMM D, YYYY")}</Text>
                </SimpleGrid>
              </Flex>

              {/* {formattedToday} ) */}
              {subTitle(
                `Activity since last statement (${dateRange(
                  passingMonthCount
                )} to ${formattedToday})`
              )}
              <Table variant="print">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Transaction</Th>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {activities.map((item, index) => (
                    <Tr key={index}>
                      <Td textAlign="center">{formatDate(item.date)}</Td>
                      <Td>{item.transaction}</Td>
                      <Td textAlign="center">{item.type}</Td>
                      <Td textAlign={"right"} fontWeight="medium">
                        {formatCurrency(item.amount)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
